import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MetaCarouselPreviewData, MetaCarouselCardPreviewData } from '../../../../../../../models/meta-template/preview-interfaces/meta-carousel-preview-data.interface';

/** One row of the debug table: the stored variable name and its current value. */
interface VariableRow {
  name: string;
  value: string;
  imageUrl?: string;
  videoUrl?: string;
}

/**
 * Scaffolding preview for the Meta Carousel Ad, every variable and each card's
 * creative in a table.
 */
@Component({
  selector: 'app-meta-carousel-variables-table',
  imports: [CommonModule],
  templateUrl: './variables-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './variables-table.component.scss'
})
export class MetaCarouselVariablesTablePreviewComponent {
  @Input() data!: MetaCarouselPreviewData;

  protected readonly failedVideos = new Set<string>();

  /** A GIF cannot play in <video>, so it falls back to <img>. */
  protected onVideoError(url: string): void {
    this.failedVideos.add(url);
  }

  /**
   * `rows` builds fresh objects on every change detection (the shell also
   * hands over a new `data` object each cycle), so without this the default
   * identity tracking tears every <tr> down and re-creates it. That rebuilds
   * the <video> too, which restarts playback on any click or hover.
   */
  protected trackByName(_index: number, row: VariableRow): string {
    return row.name;
  }

  get rows(): VariableRow[] {
    return [
      { name: 'PrimaryText', value: this.data.primaryText },
      { name: 'Text', value: this.data.ctaText },
      { name: 'AccountType', value: this.data.accountType },
      ...this.data.cards.flatMap((card, i) => this.cardRows(card, i + 1))
    ];
  }

  /** Numbered by position in the editor; the media row shows the creative inline once uploaded. */
  private cardRows(card: MetaCarouselCardPreviewData, n: number): VariableRow[] {
    const media = card.media || undefined;
    return [
      { name: `Card ${n} · Headline`, value: card.headline },
      { name: `Card ${n} · Link`, value: card.url },
      { name: `Card ${n} · Format`, value: card.format },
      {
        name: `Card ${n} · Media`,
        value: card.media,
        imageUrl: card.format === 'Image' ? media : undefined,
        videoUrl: card.format === 'Video' ? media : undefined,
      },
    ];
  }
}
